import { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useI18n } from '../../../../../context/I18nContext';
import { MAX_TOPIC_LENGTH } from '../../../../../constants/room';
import './DetailsPage.css';

const NAME_CHAR_LIMIT = 50;

const EditableField = ({
  label,
  value,
  serverValue,
  onChange,
  onSave,
  disabled,
  maxLength,
  placeholder,
  isSaving,
  error,
  t,
}) => {
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.target.blur();
    }
  };

  const handleBlur = () => {
    if (value.trim() !== serverValue) {
      onSave(value);
    }
  };

  const nearLimit = maxLength && value.length >= maxLength - 10;

  return (
    <div className="details-field">
      <div className="details-field-label">{label}</div>
      <input
        className={`details-field-input${error ? ' has-error' : ''}`}
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        disabled={disabled || isSaving}
        maxLength={maxLength}
        placeholder={placeholder}
      />
      <div className="details-field-meta">
        {maxLength && (
          <span className={`details-field-counter${nearLimit ? ' near-limit' : ''}`}>
            {value.length}/{maxLength}
          </span>
        )}
        <div className="details-field-status">
          {isSaving && (
            <>
              <span className="details-field-spinner" />
              <span className="details-field-saving">{t('RoomSettings.detailsSaving')}</span>
            </>
          )}
          {error && (
            <span className="details-field-error">{t('RoomSettings.detailsSaveError')}</span>
          )}
        </div>
      </div>
    </div>
  );
};

EditableField.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  serverValue: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  maxLength: PropTypes.number,
  placeholder: PropTypes.string,
  isSaving: PropTypes.bool,
  error: PropTypes.bool,
  t: PropTypes.func.isRequired,
};

const DetailsPage = ({
  roomId,
  roomName,
  roomTopic,
  roomVersion,
  canEditName,
  canEditTopic,
  onUpdateName,
  onUpdateTopic,
}) => {
  const { t } = useI18n();

  const [nameValue, setNameValue] = useState(roomName);
  const [topicValue, setTopicValue] = useState(roomTopic);
  const [nameServerValue, setNameServerValue] = useState(roomName);
  const [topicServerValue, setTopicServerValue] = useState(roomTopic);
  const [nameSaving, setNameSaving] = useState(false);
  const [topicSaving, setTopicSaving] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [topicError, setTopicError] = useState(false);

  // Sync from parent when server state changes externally
  useEffect(() => {
    if (!nameSaving) {
      setNameValue(roomName);
      setNameServerValue(roomName);
    }
  }, [roomName, nameSaving]);

  useEffect(() => {
    if (!topicSaving) {
      setTopicValue(roomTopic);
      setTopicServerValue(roomTopic);
    }
  }, [roomTopic, topicSaving]);

  const handleSaveName = useCallback(async (value) => {
    if (value.trim() === nameServerValue) return;
    setNameSaving(true);
    setNameError(false);
    try {
      await onUpdateName(value);
      setNameServerValue(value.trim());
    } catch {
      setNameError(true);
    } finally {
      setNameSaving(false);
    }
  }, [nameServerValue, onUpdateName]);

  const handleSaveTopic = useCallback(async (value) => {
    if (value.trim() === topicServerValue) return;
    setTopicSaving(true);
    setTopicError(false);
    try {
      await onUpdateTopic(value);
      setTopicServerValue(value.trim());
    } catch {
      setTopicError(true);
    } finally {
      setTopicSaving(false);
    }
  }, [topicServerValue, onUpdateTopic]);

  return (
    <div className="details-page room-settings-page">
      <div className="room-settings-section">
        <div className="room-settings-section-header">
          <h3 className="room-settings-section-title">{t('RoomSettings.detailsTitle')}</h3>
          <div className="room-settings-section-divider" />
        </div>

        <EditableField
          label={t('RoomSettings.detailsRoomName')}
          value={nameValue}
          serverValue={nameServerValue}
          onChange={setNameValue}
          onSave={handleSaveName}
          disabled={!canEditName}
          maxLength={NAME_CHAR_LIMIT}
          isSaving={nameSaving}
          error={nameError}
          t={t}
        />

        <EditableField
          label={t('RoomSettings.detailsRoomTopic')}
          value={topicValue}
          serverValue={topicServerValue}
          onChange={setTopicValue}
          onSave={handleSaveTopic}
          disabled={!canEditTopic}
          maxLength={MAX_TOPIC_LENGTH}
          placeholder={t('RoomSettings.detailsTopicPlaceholder')}
          isSaving={topicSaving}
          error={topicError}
          t={t}
        />

        <div className="details-field">
          <div className="details-field-label">{t('RoomSettings.detailsRoomVersion')}</div>
          <div className="details-field-value" data-testid="room-version">{roomVersion || '—'}</div>
        </div>

        <div className="details-field">
          <div className="details-field-label">{t('RoomSettings.detailsRoomId')}</div>
          <div className="details-field-value details-field-value--scrollable" data-testid="room-id">{roomId || '—'}</div>
        </div>
      </div>
    </div>
  );
};

DetailsPage.propTypes = {
  roomId: PropTypes.string,
  roomName: PropTypes.string,
  roomTopic: PropTypes.string,
  roomVersion: PropTypes.string,
  canEditName: PropTypes.bool,
  canEditTopic: PropTypes.bool,
  onUpdateName: PropTypes.func.isRequired,
  onUpdateTopic: PropTypes.func.isRequired,
};

export default DetailsPage;
